/**
 * useSubscription — react-query wrapper around GET /sparks/subscriptions/me.
 *
 * Returns `data === null` for both "not subscribed" and "503 / feature off"
 * states so call sites have one code path. A 503 means the deploy doesn't
 * have subscriptions wired up yet (local dev); the page should fall back
 * to the pack-purchase flow.
 *
 * Mirrors the existing `useSparks` shape (auth-gated + local-dev carve-out).
 * See docs/drafts/subscription-mvp-design.md.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@ponpon/api-client'
import { useAuth } from '../contexts/AuthContext'

const isLocalDev =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

export interface SubscriptionData {
  plan_tier: string
  status: string
  current_period_end: string | null
  cancel_at_period_end: boolean
  monthly_credits: number
}

async function safeGetSubscription(): Promise<SubscriptionData | null> {
  try {
    return (await api.getSubscription()) as SubscriptionData | null
  } catch (err: unknown) {
    // 503 = feature off on this deploy. Surface as "no subscription"
    // rather than letting react-query stay in `error` forever.
    if (err instanceof Error && err.message.includes('SUBSCRIPTIONS_NOT_CONFIGURED')) {
      return null
    }
    throw err
  }
}

export function useSubscription() {
  const { session } = useAuth()
  return useQuery<SubscriptionData | null>({
    queryKey: ['subscription'],
    queryFn: safeGetSubscription,
    enabled: !!session || isLocalDev,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}
